import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CertSpec, Stats, api } from "../lib/api";

function CertGrid({ certs }: { certs: CertSpec[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {certs.map((cert) => (
        <div key={cert.id} className="card flex flex-col">
          <div className="text-xs font-mono text-scribe-600 mb-1">{cert.vendor}</div>
          <div className="font-display text-lg leading-tight mb-1">{cert.name}</div>
          <div className="text-xs text-ink-700 mb-4">{cert.code}</div>
          <div className="mt-auto grid grid-cols-2 gap-2">
            <Link
              to={`/practice/${cert.id}`}
              className="btn-primary w-full px-3 text-sm whitespace-nowrap"
            >
              Pratiquer
            </Link>
            <Link
              to={`/mock-exam/${cert.id}`}
              className="btn-secondary w-full px-3 text-sm whitespace-nowrap"
            >
              Examen blanc
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [certs, setCerts] = useState<CertSpec[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.stats(), api.listCerts()])
      .then(([s, c]) => {
        setStats(s);
        setCerts(c);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-ink-700">Chargement…</div>;

  const started = (stats?.total_attempts ?? 0) > 0;
  const due = stats?.due_today ?? 0;
  const firstCert = certs[0];

  // Premier passage : personne n'a encore répondu à quoi que ce soit.
  if (!started) {
    return (
      <div>
        <div className="mb-8">
          <p className="text-xs font-mono text-scribe-600 uppercase tracking-widest mb-2">
            Bienvenue
          </p>
          <h1 className="font-display text-3xl sm:text-4xl">Commence ici.</h1>
        </div>

        <div className="card max-w-2xl mb-12">
          <p className="text-sm text-ink-700 leading-relaxed mb-4">
            Lance une session de <strong>pratique</strong> sur une certification. Chaque réponse
            crée une carte, et Thot te la représente au moment où tu es sur le point de l'oublier —
            c'est ta <strong>révision</strong> quotidienne. Quand tu te sens prêt, passe un{" "}
            <strong>examen blanc</strong> en conditions réelles.
          </p>
          {firstCert && (
            <Link to={`/practice/${firstCert.id}`} className="btn-accent inline-flex">
              Démarrer une pratique
            </Link>
          )}
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl mb-4">Tes parcours</h2>
          <CertGrid certs={certs} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <p className="text-xs font-mono text-scribe-600 uppercase tracking-widest mb-2">Ce matin</p>
        <h1 className="font-display text-3xl sm:text-4xl">
          {due > 0 ? `${due} ${due > 1 ? "cartes" : "carte"} à réviser.` : "Rien à réviser. Bravo."}
        </h1>
      </div>

      {due > 0 ? (
        <Link to="/review" className="btn-accent inline-block mb-12">
          Commencer la révision
        </Link>
      ) : (
        <div className="mb-12">
          <p className="text-sm text-scribe-600 mb-3">
            Tes cartes reviendront d'elles-mêmes. En attendant, tu peux prendre de l'avance.
          </p>
          {firstCert && (
            <Link to={`/practice/${firstCert.id}`} className="btn-secondary inline-flex">
              Pratique libre
            </Link>
          )}
        </div>
      )}

      <div className="mt-12">
        <h2 className="font-display text-2xl mb-4">Tes parcours</h2>
        <CertGrid certs={certs} />
      </div>
    </div>
  );
}
